<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <header class="card-header">
            <div class="d-flex justify-content-between align-items-center">
              <strong>Configuração de Prompts — IA PMAU</strong>
            </div>
          </header>

          <div v-if="loading" class="loading-container">
            <RotateSquare class="loading-position animated fadeIn" size="60px" />
          </div>

          <div v-else class="card-body">
            <!-- Filtros -->
            <div class="row mb-3">
              <div class="col-lg-4 col-md-5 col-sm-12">
                <div class="form-group">
                  <label>Filtrar por grupo</label>
                  <b-form-select
                    v-model="filtroGrupo"
                    :options="opcoesGrupo"
                    @change="filtrar"
                  />
                </div>
              </div>
              <div class="col-lg-4 col-md-5 col-sm-12">
                <div class="form-group">
                  <label>Filtrar por tipo</label>
                  <b-form-select
                    v-model="filtroTipo"
                    :options="opcoesTipo"
                    @change="filtrar"
                  />
                </div>
              </div>
              <div class="col-lg-4 col-md-2 col-sm-12 d-flex align-items-end pb-3">
                <button class="btn btn-secondary" @click="limparFiltros">Limpar</button>
              </div>
            </div>

            <!-- Tabela -->
            <b-table
              :hover="true"
              responsive
              :items="itensFiltrados"
              :fields="fields"
              striped
              show-empty
              empty-text="Nenhum prompt encontrado."
            >
              <template v-slot:empty="scope">
                <h4>{{ scope.emptyText }}</h4>
              </template>

              <template v-slot:cell(stage_code)="data">
                {{ labelEtapa(data.item.stage_code) }}
              </template>

              <template v-slot:cell(prompt_type)="data">
                <b-badge :variant="tipoBadgeVariant(data.item.prompt_type)">
                  {{ data.item.prompt_type }}
                </b-badge>
              </template>

              <template v-slot:cell(is_active)="data">
                <b-badge :variant="data.item.is_active ? 'success' : 'secondary'">
                  {{ data.item.is_active ? "Ativo" : "Inativo" }}
                </b-badge>
              </template>

              <template v-slot:cell(updated_at)="data">
                {{ formatarData(data.item.updated_at) }}
              </template>

              <template v-slot:cell(acoes)="data">
                <b-button
                  variant="info"
                  size="sm"
                  title="Editar"
                  @click="abrirEdicao(data.item)"
                >
                  <i class="fa fa-edit"></i>
                </b-button>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Edição -->
    <b-modal
      v-model="modalAberto"
      size="xl"
      scrollable
      :title="itemEdicao ? itemEdicao.name : 'Editar Prompt'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      ok-variant="primary"
      :ok-disabled="salvando"
      @ok="salvar"
      @hidden="fecharModal"
    >
      <div v-if="itemEdicao">
        <!-- Metadados (somente leitura) -->
        <div class="row mb-2">
          <div class="col-md-6">
            <small class="text-muted">
              <strong>Etapa:</strong>
              <code class="ml-1">{{ itemEdicao.stage_code }}</code>
            </small>
          </div>
          <div class="col-md-6">
            <small class="text-muted">
              <strong>Tipo:</strong>
              <b-badge :variant="tipoBadgeVariant(itemEdicao.prompt_type)" class="ml-1">
                {{ itemEdicao.prompt_type }}
              </b-badge>
            </small>
          </div>
        </div>

        <div v-if="itemEdicao.description" class="alert alert-info py-2 small mb-3">
          {{ itemEdicao.description }}
        </div>

        <!-- Status -->
        <div class="form-group">
          <b-form-checkbox v-model="edicaoAtivo">Prompt ativo</b-form-checkbox>
          <small class="text-muted">Prompts inativos são ignorados pelo sistema de geração.</small>
        </div>

        <!-- Variáveis disponíveis -->
        <div class="alert alert-secondary py-2 mb-3">
          <small>
            <strong>Variáveis disponíveis</strong>
            <span class="text-muted ml-1">(use a sintaxe <code>{variavel}</code> no prompt)</span>
          </small>
          <div class="mt-2">
            <div class="mb-1">
              <small class="text-muted d-block mb-1">Geração (todos os tipos):</small>
              <code
                v-for="v in variavelsConta"
                :key="v"
                class="mr-2 mb-1 d-inline-block bg-light border rounded px-1 cursor-pointer"
                style="font-size: 0.8em;"
                :title="'Clique para copiar: ' + v"
                @click="copiarVariavel(v)"
              >{{ '{' + v + '}' }}</code>
            </div>
            <div v-if="variaveisPmau.length" class="mt-1">
              <small class="text-muted d-block mb-1">Variáveis PMAU (configuradas por conta):</small>
              <code
                v-for="v in variaveisPmau"
                :key="'pmau-' + v"
                class="mr-2 mb-1 d-inline-block bg-light border border-primary rounded px-1 cursor-pointer"
                style="font-size: 0.8em;"
                :title="'Clique para copiar: ' + v"
                @click="copiarVariavel(v)"
              >{{ '{' + v + '}' }}</code>
            </div>
            <div v-if="variaveisExtras(itemEdicao).length" class="mt-1">
              <small class="text-muted d-block mb-1">Específicas desta etapa/tipo:</small>
              <code
                v-for="v in variaveisExtras(itemEdicao)"
                :key="v"
                class="mr-2 mb-1 d-inline-block bg-light border rounded px-1 cursor-pointer"
                style="font-size: 0.8em;"
                :title="'Clique para copiar: ' + v"
                @click="copiarVariavel(v)"
              >{{ '{' + v + '}' }}</code>
            </div>
          </div>
        </div>

        <!-- Conteúdo -->
        <div class="form-group">
          <label>
            <strong>Conteúdo do Prompt</strong>
            <small class="text-muted ml-2">({{ edicaoConteudo.length }} caracteres)</small>
          </label>
          <b-form-textarea
            v-model="edicaoConteudo"
            rows="20"
            style="font-family: monospace; font-size: 0.85em; resize: vertical;"
            placeholder="Conteúdo do prompt..."
          />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import PmauService from "../../servico/PmauService";

const STAGE_LABELS = {
  "__global__": "Global",
  "introduction_history": "1.1 Histórico",
  "introduction_importance": "1.2 Importância",
  "introduction_objectives": "1.3 Objetivos",
  "characterization_location": "2.1 Localização",
  "characterization_vegetation": "2.2.1 Vegetação",
  "characterization_hydrography": "2.2.2 Hidrografia",
  "characterization_soil": "2.2.3 Solo",
  "characterization_climate": "2.3 Clima",
  "characterization_population": "2.4 População",
  "characterization_socioeconomic": "2.5 Socioeconômico",
  "characterization_urban_area": "2.6 Malha Urbana",
  "characterization_legislation": "2.7 Legislação",
  "participatory_diagnosis": "3. Diagnóstico Participativo",
  "forest_inventory_methodology": "4.1 Metodologia Utilizada",
  "forest_inventory_general_quantitative": "4.2.1 Quantitativo Geral",
  "forest_inventory_dendrometric_variables": "4.2.2 Variáveis Dendrométricas",
  "forest_inventory_species": "4.2.3 Espécies Encontradas",
  "forest_inventory_origin": "4.2.4 Origem das Espécies",
  "forest_inventory_threatened_species": "4.2.5 Espécies Ameaçadas e/ou Endêmicas",
  "forest_inventory_relevant_species": "4.2.6 Espécie Relevante",
  "forest_inventory_main_problems": "4.3 Principais Problemas Encontrados",
  "forest_inventory_gaau_index": "4.4 Índice GAAU – Grau de Atenção para Árvores Urbanas",
  "urban_planning_intro": "5. Planejamento da Arborização Urbana",
  "urban_planning_spaces": "5.1 Espaços e Ambientes",
  "urban_planning_planting_criteria": "5.2 Critérios de Plantio",
  "urban_planning_sidewalk_width": "5.2.1 Largura do Passeio",
  "urban_planning_property": "5.2.2 Imóvel",
  "urban_planning_electrical": "5.2.3 Rede Elétrica",
  "urban_planning_road_use": "5.2.4 Uso da Via",
  "urban_planning_urban_furniture": "5.2.5 Mobiliário Urbano",
  "urban_planning_spacing": "5.2.6 Espaçamento entre Árvores",
  "urban_planning_tree_characteristics": "5.3 Características Gerais das Árvores",
  "urban_planning_tree_size": "5.3.1 Porte das Árvores",
  "urban_planning_crown_architecture": "5.3.2 Arquitetura de Copa",
  "urban_planning_flowers_fruits": "5.3.3 Flores e Frutos",
  "urban_planning_roots": "5.3.4 Raízes",
  "urban_planning_trunk": "5.3.5 Tronco",
  "urban_planning_origin": "5.4 Origem – Nativas e Exóticas",
  "urban_planning_species": "5.5 Critério de Escolha de Espécies",
  "urban_planning_indicated_species": "5.6 Espécies Indicadas",
  "urban_planning_toxic_plants": "5.7.1 Plantas Tóxicas",
  "urban_planning_invasive_species": "5.7.2 Espécies Exóticas Invasoras",
  "fall_risk_assessment_levels": "8.1 Níveis de Avaliação de Risco",
  "fall_risk_assessment_methodology": "8.2 Metodologia para Avaliação de Risco de Queda",
  "fall_risk_technical_responsibility": "8.3 Responsabilidade Técnica",
};

const TYPE_VARIANT = {
  system: "dark",
  user: "primary",
  queries: "warning",
  insert: "info",
  fixed_text: "secondary",
  options: "success",
  html_directives: "light",
  html_directives_location: "light",
  queries_system: "warning"
};

export default {
  name: "PmauPrompts",
  components: { RotateSquare },
  data() {
    return {
      loading: false,
      salvando: false,
      itens: [],
      filtroGrupo: null,
      filtroTipo: null,
      modalAberto: false,
      itemEdicao: null,
      edicaoConteudo: "",
      edicaoAtivo: true,
      variavelsConta: ["conta_name"],
      variaveisPmau: [],
      fields: [
        { key: "stage_code", label: "Etapa", sortable: true },
        { key: "prompt_type", label: "Tipo", sortable: true },
        { key: "name", label: "Nome", sortable: true },
        { key: "is_active", label: "Status", sortable: true },
        { key: "updated_at", label: "Atualizado em", sortable: true },
        { key: "acoes", label: "Ações", sortable: false, thClass: "wd-80-px" }
      ]
    };
  },
  computed: {
    opcoesGrupo() {
      const grupos = [...new Set(this.itens.map((i) => i.stage_code))];
      grupos.sort((a, b) => (STAGE_LABELS[a] || a).localeCompare(STAGE_LABELS[b] || b, "pt-BR"));
      return [
        { value: null, text: "Todos os grupos" },
        ...grupos.map((g) => ({ value: g, text: STAGE_LABELS[g] || g }))
      ];
    },
    opcoesTipo() {
      const tipos = [...new Set(this.itens.map((i) => i.prompt_type))];
      tipos.sort((a, b) => a.localeCompare(b, "pt-BR"));
      return [
        { value: null, text: "Todos os tipos" },
        ...tipos.map((t) => ({ value: t, text: t }))
      ];
    },
    itensFiltrados() {
      return this.itens
        .filter((item) => {
          if (this.filtroGrupo && item.stage_code !== this.filtroGrupo) return false;
          if (this.filtroTipo && item.prompt_type !== this.filtroTipo) return false;
          return true;
        })
        .sort((a, b) => {
          const labelA = STAGE_LABELS[a.stage_code] || a.stage_code;
          const labelB = STAGE_LABELS[b.stage_code] || b.stage_code;
          const byLabel = labelA.localeCompare(labelB, "pt-BR");
          if (byLabel !== 0) return byLabel;
          return a.prompt_type.localeCompare(b.prompt_type, "pt-BR");
        });
    }
  },
  mounted() {
    this.carregarPrompts();
    this.carregarVariaveis();
  },
  methods: {
    carregarPrompts() {
      this.loading = true;
      PmauService.ListPrompts()
        .then((response) => {
          this.itens = response.data;
        })
        .catch((erro) => {
          this.$notify({
            data: ["Erro ao carregar prompts."],
            type: "warn",
            duration: 5000
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    carregarVariaveis() {
      PmauService.ListVariables()
        .then((r) => {
          this.variaveisPmau = r.data.map((v) => v.code);
        })
        .catch(() => {});
    },
    filtrar() {
      // computed já filtra reativamente
    },
    limparFiltros() {
      this.filtroGrupo = null;
      this.filtroTipo = null;
    },
    labelEtapa(code) {
      return STAGE_LABELS[code] || code;
    },
    tipoBadgeVariant(tipo) {
      return TYPE_VARIANT[tipo] || "secondary";
    },
    formatarData(isoString) {
      if (!isoString) return "";
      return new Date(isoString).toLocaleString("pt-BR");
    },
    abrirEdicao(item) {
      this.itemEdicao = item;
      this.edicaoConteudo = item.content;
      this.edicaoAtivo = item.is_active;
      this.modalAberto = true;
    },
    fecharModal() {
      this.itemEdicao = null;
      this.edicaoConteudo = "";
      this.edicaoAtivo = true;
    },
    variaveisExtras(item) {
      if (!item) return [];
      if (item.prompt_type === "user") {
        if (item.stage_code === "introduction_objectives")
          return ["objectives_fixed_text", "objectives_list", "html_directives"];
        if (item.stage_code === "participatory_diagnosis")
          return ["cidade", "survey_data", "html_directives"];
        return ["html_directives"];
      }
      if (item.prompt_type === "queries") {
        if (item.stage_code === "urban_planning_indicated_species"
          || item.stage_code === "urban_planning_toxic_plants")
          return ["text_content"];
        return ["cidade", "text_content"];
      }
      if (item.prompt_type === "insert") {
        if (item.stage_code === "characterization_vegetation")
          return ["images_group1_json", "images_group2_json", "text_content"];
        if (item.stage_code === "urban_planning_indicated_species"
          || item.stage_code === "urban_planning_toxic_plants")
          return ["title", "source", "images_json"];
        return ["images_json", "text_content"];
      }
      return [];
    },
    copiarVariavel(variavel) {
      const texto = "{" + variavel + "}";
      navigator.clipboard.writeText(texto).then(() => {
        this.$notify({
          data: [`Copiado: ${texto}`],
          type: "success",
          duration: 2000
        });
      });
    },
    salvar(evento) {
      evento.preventDefault();

      if (!this.edicaoConteudo.trim()) {
        this.$notify({
          data: ["O conteúdo do prompt não pode estar vazio."],
          type: "warn",
          duration: 3000
        });
        return;
      }

      this.salvando = true;
      PmauService.UpdatePrompt(
        this.itemEdicao.stage_code,
        this.itemEdicao.prompt_type,
        {
          content: this.edicaoConteudo,
          is_active: this.edicaoAtivo
        }
      )
        .then((response) => {
          const idx = this.itens.findIndex(
            (i) =>
              i.stage_code === this.itemEdicao.stage_code &&
              i.prompt_type === this.itemEdicao.prompt_type
          );
          if (idx !== -1) {
            this.$set(this.itens, idx, response.data);
          }
          this.modalAberto = false;
          this.$notify({
            data: ["Prompt atualizado com sucesso."],
            type: "success",
            duration: 4000
          });
        })
        .catch(() => {
          this.$notify({
            data: ["Erro ao salvar prompt."],
            type: "warn",
            duration: 5000
          });
        })
        .finally(() => {
          this.salvando = false;
        });
    }
  }
};
</script>
